import { Navigate, useParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ContentListPage } from '../components/ContentListPage';
import { FeatureArticleHero } from '../components/feature/FeatureArticleHero';
import { RecentFeaturesSidebar } from '../components/feature/RecentFeaturesSidebar';
import { ArticleContent } from '../components/article/ArticleBlockRenderer';
import {
  ArticleHeroMedia,
  articleHasHeroMedia,
} from '../components/article/ArticleHeroMedia';
import { useCachedPublicGet } from '../hooks/useCachedPublicGet';
import { useCachedPublishedArticles } from '../hooks/useCachedPublishedArticles';
import type { ArticleRow } from '../../../shared/schemas/article';

export function FeaturePage() {
  const { slug } = useParams<{ slug: string }>();
  const articlePath = slug ? `/api/articles/${slug}` : undefined;
  const {
    data: article,
    loading: articleLoading,
    error: articleError,
  } = useCachedPublicGet<ArticleRow>(articlePath);
  const { data: recentFeatures } = useCachedPublishedArticles(
    '/api/articles?type=feature&limit=10',
  );

  const isReady = Boolean(article) && !articleLoading;
  const sidebarReady = recentFeatures !== null;
  const contentReady = isReady && sidebarReady;
  const showError = !articleLoading && Boolean(articleError || (slug && !article));
  const hasHeroMedia = article
    ? articleHasHeroMedia({
        hero_media: article.hero_media,
        mini_demo_id: article.mini_demo_id,
      })
    : false;

  if (isReady && article && article.type !== 'feature') {
    return <Navigate to={`/articles/${slug}`} replace />;
  }

  const leftPanel = sidebarReady ? (
    <RecentFeaturesSidebar entries={recentFeatures} currentSlug={slug ?? ''} />
  ) : null;

  if (showError) {
    return (
      <ContentListPage title="Feature not found" leftPanel={leftPanel}>
        <p className="text-body text-text-tertiary">
          {articleError ?? 'Feature not found'}
        </p>
        <a href="/features" className="text-caption link-default">
          Browse all features
        </a>
      </ContentListPage>
    );
  }

  return (
    <ContentListPage
      title={article?.title ?? ''}
      contentReady={contentReady}
      leftPanel={leftPanel}
    >
      {article && (
        <>
          <FeatureArticleHero>
            {hasHeroMedia ? (
              <ArticleHeroMedia
                heroMedia={article.hero_media ?? undefined}
                legacyMiniDemoId={article.mini_demo_id}
                demoFallbackClassName="h-full w-full"
              />
            ) : undefined}
          </FeatureArticleHero>

          <ArticleContent content={article.content} />
          <a href="/features" className="site-btn-secondary site-btn-md">
            Browse all features
            <ArrowUpRight size={14} />
          </a>
        </>
      )}
    </ContentListPage>
  );
}
